import { ActivatedChallengePurchasesResponse, AdditionalActivatedChallengeFields, ChallengeLocationFilters, ChallengeLocationSummary, ChallengeType, ChargebeeTransactionStatus, CheckIn, CheckinInfo, CountedCheckIn, CustomShape, FeedPhotoCardDetails, GetActivatedChallengeResponse, GetChallengeResponse, GetPointResponse, GetPublicOrganizationProductResponse, Location, OrganizationProductStatus, PaymentTargetType, PurchaseStatus } from '../../types';
import { addActivatedChallenge, getActivatedChallenge, getActivatedChallengesForAccount, getActivatedChallengesForChallenge, getChallenge, get_hunt_give_up, get_hunt_hint } from '../api/challenge-api';
import { areLocationsSame, categoryFilterFn, keywordSearchFn, tagsFilterFn } from '../filters';
import { createInternalChallengeNote, deleteInternalChallengeNote, getInternalChallengeNotes, updateInternalChallengeNote } from '../api/comments-api';
import { getCheckinKey, getCheckinMapFromCheckins } from '../api/checkin-api';

import { ChallengePage } from '../../pages/challenge-page';
import { DEFAULT_CATEGORY_ICON, NO_REGISTRATION_FOUND_NOTICE, REGISTRATION_NOTICE_KEY } from '../constants';
import { State } from '../state/state-management/state';
import accountInfo from './account-info';
import { createGoogleMapShapeFromCustomShape } from '../shapes';
import { getMainPage } from '../page-name';
import { getOrganizationProductsPublic } from '../api/products-api';
import { get_activated_challenge_purchases } from '../api/purchases-api';
import { isDefined, setSessionStorage } from '../utils';
import { isNumber } from '@turf/helpers';
import pointCache from './point-cache';
import { property } from '../state/state-management/decorators/property';
import { state } from 'lit/decorators';
import topicData from './topic-data';

class ChallengeData extends State {
    @property({ type: Boolean }) isReady: boolean = false;

    @property({ type: Object}) challenge?: GetChallengeResponse;

    @property({ type: Array }) activated_challenges: GetActivatedChallengeResponse[] =[];

    @property({ type: Map }) allCheckins: Map<String, CheckIn[]> = new Map<
        String,
        CheckIn[]
    >();

    @property({ type: Map }) CountedCheckIns: Map<String, CountedCheckIn[]> = new Map<
        String,
        CountedCheckIn[]
    >();

    @property({ type: String }) activatedChallengeId?: string;

    @property({ type: String }) activatedChallengeKey?: string;

    @property({ type: Array}) challengeLocations: ChallengeLocationSummary[] = [];

    @property({ type: Object}) challenge_page?: ChallengePage;

    @state() filteredChallengeLocations: ChallengeLocationSummary[] = [];

    @state() activated_challenge?: GetActivatedChallengeResponse;

    @state() activated_checkin_details: CheckinInfo[] = [];

    @state() thisUserPurchases: ActivatedChallengePurchasesResponse | undefined;

    @state() location_sort_filter: ChallengeLocationFilters = {
        nearby_sort: undefined,
        distance: undefined,
        active: true,
        checked_in: 'all',
        geo_location_eligible: false,
        value_sort: undefined,
        point_value_min: undefined,
        point_value_max: undefined,
        category_names: [],
        tags: undefined,
        search: undefined
    };

    @state() products: GetPublicOrganizationProductResponse[] = [];

    static amplitude_callsite: string = 'Challenge Cache';

    // call this in sign in page, and then in main-page. in main-page, check if data isReady, if not, instantiate
    async instantiateChallengeData(challenge_id: string, topic_key?: string,  activated_challenge_id?: string, activated_challenge_key?: string, get_user_location: boolean = false) {
        this.isReady = false;

        this.challenge = await getChallenge(ChallengeData.amplitude_callsite, this.activated_challenge?.challenge_id.$oid ?? challenge_id, topicData.topicKey, activated_challenge_id, activated_challenge_key);

        this.activatedChallengeKey = activated_challenge_key;
        this.activatedChallengeId = activated_challenge_id;

        try {
            await this.setActivatedChallengeForUser();
        } catch (e) {
            // if it cant find it it may have been deleted. remove id and key
            getMainPage().errorToastElement?.showToast('Failed to get challenge for user - please register. If this issue persists, please contact support.');
            this.handleDeletedActivatedChallenge();
        }

        if (!isDefined(this.challenge)) {
            console.error('Challenge not found');
            this.isReady = true;
            return;
        }

        await Promise.all([
            this.maybeGetActivatedChallenges(),
            this.setChallengeLocations(get_user_location && this.challenge?.geofence_radius_m !== undefined),
            this.getProductsForChallenge(),
        ]);

        // apply the filters
        this.applySortFilter();
        this.isReady = true;
    }

    async maybeGetActivatedChallenges() {
        if (topicData.isWriter || this.challenge?.show_leaderboard === true || this.challenge?.show_photo_feed === true) {
            try {
                const activated_challenges = await getActivatedChallengesForChallenge(ChallengeData.amplitude_callsite, this.challenge?._id.$oid ?? '', topicData.topicKey);
                this.activated_challenges = activated_challenges;
            } catch (e) {
                this.activated_challenges = [];
            }
        } else {
            this.activated_challenges = [];
        }
    }

    // for use when making changes to the challenge or activated challenge
    async refreshChallengeData() {
        this.challenge = await getChallenge(ChallengeData.amplitude_callsite, this.challenge?._id.$oid ?? '', topicData.topicKey, this.activatedChallengeId, this.activatedChallengeKey);

        if (!isDefined(this.challenge)) {
            console.error('Challenge not found');
            return;
        }

        await Promise.all([
            this.maybeGetActivatedChallenges(),
            this.setChallengeLocations(false),
            this.getProductsForChallenge(),
        ]);

        // apply the filters
        this.applySortFilter();
    }

    get isPaymentRequired(): boolean {
        if (!isDefined(this.challenge)) {
            return false;
        }
        for (const product of this.products) {
            if ('Fixed' in product.pricing_structure && product.status === OrganizationProductStatus.Active) {
                return true;
            }
            if ('Donation' in product.pricing_structure && product.pricing_structure.Donation.required && product.status === OrganizationProductStatus.Active) {
                return true;
            }
            if ('Recurring' in product.pricing_structure && product.status === OrganizationProductStatus.Active) {
                return true;
            }
        }
        return false;
    }

    get hasNonRequiredDonationOption(): boolean {
        if (!isDefined(this.challenge)) {
            return false;
        }
        for (const product of this.products) {
            if ('Donation' in product.pricing_structure && !product.pricing_structure.Donation.required && product.status === OrganizationProductStatus.Active) {
                return true;
            }
        }
        return false;
    }

    get hasMadeDonation(): boolean {
        if (!isDefined(this.thisUserPurchases)) {
            return false;
        }
        return this.thisUserPurchases.transaction_history.some(
            (purchase) => purchase.status === PurchaseStatus.Completed &&
                purchase.purchase_items.some(
                    (item) => this.products.some(
                        (product) => product._id.$oid === item.product_id.$oid &&
                            'Donation' in product.pricing_structure
                    )
            )
        );
    }

    handleDeletedActivatedChallenge() {
        this.activatedChallengeId = undefined;
        this.activatedChallengeKey = undefined;
        this.activated_challenge = undefined;
        topicData.activatedChallengeId = undefined;
        topicData.activatedChallengeKey = undefined;

        const activatedChallengeStorageId = localStorage.getItem(`activated_challenge_${this.challenge?._id.$oid ?? ''}_id`);
        const activatedChallengeStorageKey = localStorage.getItem(`activated_challenge_${this.challenge?._id.$oid ?? ''}_write_key`);

        // remove from local storage — including the "registered" flag, otherwise the registration
        // page keeps showing the "log in to confirm" state for a registration that no longer exists.
        localStorage.removeItem(`activated_challenge_${this.challenge?._id.$oid ?? ''}`);
        if (isDefined(activatedChallengeStorageId)) {
            localStorage.removeItem(`activated_challenge_${this.challenge?._id.$oid ?? ''}_id`);
        }
        if (activatedChallengeStorageKey) {
            localStorage.removeItem(`activated_challenge_${this.challenge?._id.$oid ?? ''}_write_key`);
        }

        // If they had a registration on this device that's now gone, let the registration page explain
        // it once rather than silently dropping them on a blank form.
        if (isDefined(activatedChallengeStorageId)) {
            setSessionStorage(REGISTRATION_NOTICE_KEY, NO_REGISTRATION_FOUND_NOTICE);
        }
    }

    get displayOneAtATime() {
        return this.challenge?.scavenger_hunt_settings?.display_one_clue_at_a_time === true || this.challenge?.challenge_type === 'trivia';
    }

    get placesLeaderboard(): Array<{ id: string, count: number }> {
        // Gather all counted checkins for each location on activated challenges
        const countedCheckins: CountedCheckIn[] = [];
        this.activated_challenges.forEach((activatedChallenge) => {
            countedCheckins.push(...activatedChallenge.counted_checkins ?? []);
        });

        // Group by location id and count
        const locationCounts = new Map<string, number>();
        countedCheckins.forEach((checkin) => {
            const locationIdStr = checkin.location_id.$oid;
            locationCounts.set(locationIdStr, (locationCounts.get(locationIdStr) ?? 0) + 1);
        });

        // Sort by count descending and return as { id, count }
        return Array.from(locationCounts.entries())
            .map(([id, count]) => ({ id, count }))
            .sort((a, b) => b.count - a.count);
    }

    get allPublicChallengeImages(): FeedPhotoCardDetails[] {
        const all_images: FeedPhotoCardDetails[] = this.activated_challenges.filter(challenge =>
            challenge.images && challenge.images.length > 0 && !challenge.hidden_from_leaderboard
        ).map((challenge) => {
            const images = challenge.images;
            if (images && images.length > 0) {
                const pendingLocations = challenge.pending_checkin_location_ids ?? [];
                const pendingActivities = challenge.activities?.filter(activity => !activity.approved).map(activity => activity.activity_id.$oid) ?? [];
                const pendingIds = [...pendingLocations, ...pendingActivities];
                return images
                    .filter(image => image.can_be_displayed && image.image_url && !pendingIds?.includes(image.challenge_location_id.$oid))
                    .map((image) => ({
                        activated_challenge_id: challenge._id,
                        image_url: image.image_url ?? '',
                        type: 'Location',
                        location_or_activity_id: image.challenge_location_id ?? '',
                        captured_at: image.captured_at ?? '',
                        username: challenge.display_name ?? 'anonymous',
                    }));
            }
            return [];
        }).flat();

        // Remove duplicate image URLs (keep the most recent one)
        const seenUrls = new Set<string>();
        const uniqueImages = all_images.filter((image) => {
            if (seenUrls.has(image.image_url)) {
                return false;
            }
            seenUrls.add(image.image_url);
            return true;
        });

        // sort by captured_at, most recent first
        uniqueImages.sort((a, b) => {
            if (!a.captured_at || !b.captured_at) {
                return 0;
            }
            return new Date(b.captured_at).getTime() - new Date(a.captured_at).getTime();
        });

        return uniqueImages;
    }

    // also needs to be called after point cache is ready
    async setChallengeLocations(get_user_location: boolean = false) {
        this.bindChallengeLocationsToPoints(pointCache.getSortedPoints(undefined, true));
        if (get_user_location) {
            await this.refreshGeoEligibility();
        } else {
            this.filteredChallengeLocations = this.challengeLocations.filter((location) => location.challenge_location_info?.is_active !== false);
        }
        await this.refreshCheckins();
    }

    bindChallengeLocationsToPoints(points: GetPointResponse[]) {
        if (!isDefined(this.challenge)) return;

        const challengeLocationsSummary: ChallengeLocationSummary[] = [];

        this.challenge?.locations.forEach((location) => {
            if (!isDefined(location.location)) {
                challengeLocationsSummary.push({
                    point_info: undefined,
                    challenge_location_info: location,
                    icon: undefined,
                    checked_in: undefined,
                    geo_location_eligible: false,
                    distance: undefined,
                    gave_up: false,
                    used_hint: false,
                    earned_points: 0
                });
            } else {
                const point = points.find(p => areLocationsSame(p.location, location.location));
                if (point) {
                    challengeLocationsSummary.push({
                        point_info: point,
                        challenge_location_info: location,
                        icon: undefined,
                        checked_in: undefined,
                        geo_location_eligible: false,
                        distance: undefined,
                        gave_up: false,
                        used_hint: false,
                        earned_points: 0
                    });
                } else {
                    if (topicData.isWriter) {
                        console.warn(`Point not found for location: ${location.location.place_id}`);
                        challengeLocationsSummary.push({
                            point_info: undefined,
                            challenge_location_info: location,
                            icon: undefined,
                            checked_in: undefined,
                            geo_location_eligible: false,
                            distance: undefined,
                            gave_up: false,
                            used_hint: false,
                            earned_points: 0
                        });
                    }
                }
            }
        });

        // orger challenge locations by point order
        this.challengeLocations = challengeLocationsSummary.sort((a, b) => {
            const indexA = points.findIndex(point => point === a.point_info);
            const indexB = points.findIndex(point => point === b.point_info);
            return indexA - indexB;
        });
    }

    async refreshChallenge() {
        if (this.challenge) {
            await pointCache.challengePointRefresh();
            this.challenge = await getChallenge(ChallengeData.amplitude_callsite, this.challenge?._id.$oid, topicData.topicKey, this.activatedChallengeId, this.activatedChallengeKey);
            if (!isDefined(this.challenge)) {
                console.error('Challenge not found');
                return;
            }
            // refresh the challenge locations
            await Promise.all([
                this.setChallengeLocations(),
                this.maybeGetActivatedChallenges()
            ]);
        }

        if (this.activatedChallengeId) {
            this.activated_challenge = await getActivatedChallenge(ChallengeData.amplitude_callsite, this.activatedChallengeId, this.activatedChallengeKey);
            await this.refreshCheckins();
        }

        // reapply the filters
        this.applySortFilter();
        try {
            await this.getThisUserPurchases(true);
        } catch (error) {
            console.error('Error getting user purchases:', error);
        }
    }

    async refreshActivatedChallenges() {
        await this.maybeGetActivatedChallenges();
    }

    async refreshGeoEligibility() {
        const locations = await this.getLocationsWithGeoEligibility();
        this.challengeLocations = locations.filter((location) => location.challenge_location_info?.is_active !== false);
        this.filteredChallengeLocations = this.challengeLocations;
    }

    async addActivatedChallenge(
        amplitude_callsite: string,
        email: string,
        phoneNumber?: string,
        display_name?: string,
        sms_opted_out: boolean = false,
        additional_fields?: AdditionalActivatedChallengeFields[]
    ) {
        if (!this.challenge) {
            console.error('Challenge not found');
            throw new Error('Challenge not found');
        }
        const result = await addActivatedChallenge(amplitude_callsite, email, this.challenge?._id.$oid, phoneNumber, display_name, sms_opted_out, additional_fields, topicData.topicKey);

        this.activatedChallengeKey = result.ac_write_key;
        this.activatedChallengeId = result._id.$oid;

        // push state with new link
        // replaceStateWithPageName(result.link_set.activated_challenge_map_write_link, `${display_name ?? 'user'} - ${this.challenge.name}`);

        return result;
    };

    async setActivatedChallengeForUser() {
        if (this.activatedChallengeId) {
            const activated_challenge = await getActivatedChallenge(ChallengeData.amplitude_callsite, this.activatedChallengeId, this.activatedChallengeKey);
            this.activated_challenge = activated_challenge;

        } else if (accountInfo.isAuthenticated) {
            const all_account_challenges = await getActivatedChallengesForAccount(ChallengeData.amplitude_callsite, accountInfo.email);
            this.activated_challenge = all_account_challenges?.find(
                (challenge) => challenge.challenge_id.$oid === this.challenge?._id.$oid
            );
            this.activatedChallengeId = this.activated_challenge?._id.$oid;
        }

        if (this.activatedChallengeId) {
            try {
                await this.getThisUserPurchases(true);
            } catch (error) {
                console.error('Error getting user purchases:', error);
            }
        }
    }

    hasActivatedChallenge() {
        return this.activated_challenge;
    }

    async refreshCheckins() {
        let checkins = new Map<String, CheckIn[]>();
        if (accountInfo.isAuthenticated) {
            checkins = await accountInfo.getAccountCheckins();
        }
        if (this.activatedChallengeId && isDefined(this.activatedChallengeKey) && isDefined(this.challenge?.topic_id)) {
            const activated_challenge = await getActivatedChallenge(ChallengeData.amplitude_callsite, this.activatedChallengeId, this.activatedChallengeKey);
            if (activated_challenge.checkins) {
                const ac_checkins = getCheckinMapFromCheckins(activated_challenge.checkins);
                // If the activated challenge has check-ins, merge them with the account check-ins
                if (checkins.size > 0) {
                    ac_checkins.forEach((checkInArray, key) => {
                        if (checkins.has(key)) {
                            checkins.get(key)?.push(...checkInArray);
                        } else {
                            checkins.set(key, checkInArray);
                        }
                    });
                } else {
                    checkins = ac_checkins;
                }
            }
        }

        // Sort check-ins by timestamp (newest first) for each location
        checkins.forEach((checkIns, key) => {
            checkIns.sort((a, b) => {
                const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                return timeB - timeA; // Newest first
            });
        });

        this.allCheckins = checkins;
        this.getCheckinsDetails(pointCache.allPoints);

        this.challengeLocations.map((location) => {
            if (!isDefined(location.point_info)) {
                return location;
            }

            // Get all check-ins for this location
            const locationKey = getCheckinKey(location.point_info.location);
            const allCheckIns = this.allCheckins.get(locationKey) ?? [];
            // Use the most recent check-in for compatibility with existing code
            const checkin = allCheckIns.length > 0 ? allCheckIns[0] : undefined;

            if (this.activated_challenge && checkin) {
                if (!isDefined(checkin.timestamp)) {
                    location.checked_in = true;
                } else {
                    // Testers can check in before the challenge starts — only their personal reset bounds them.
                    const challenge_start_time = this.activated_challenge.is_tester ? '' : (this.challenge?.start_time ?? '');
                    const reset_at = this.activated_challenge.reset_at ?? '';
                    const most_recent_date = challenge_start_time > reset_at ? challenge_start_time : reset_at;
                    location.checked_in = isDefined(checkin) && ((checkin.timestamp ?? (new Date).toISOString) > most_recent_date);
                }
            } else {
                // Handle the case where activated_challenge is undefined
                location.checked_in = false;
            }
            location.gave_up = this.gaveUpIds.includes(location.challenge_location_info?._id?.$oid ?? '');
            location.used_hint = this.usedHintIds.includes(location.challenge_location_info?._id?.$oid ?? '');

            let earned_points = 0;
            if (location.checked_in && !location.gave_up) {
                if (this.activated_challenge?.counted_checkins && this.activated_challenge?.counted_checkins.length > 0) {
                    const all_valid_checkins = this.activated_challenge.counted_checkins.filter(checkin => checkin.location_id.$oid === location.challenge_location_info?._id?.$oid);
                    const totalPoints = all_valid_checkins.reduce((acc, checkin) => acc + (checkin.point_value ?? 0), 0);
                    earned_points = totalPoints;
                } else {
                    earned_points = location.challenge_location_info?.value ?? 0;
                }
            }
            if (location.used_hint && location.checked_in) {
                earned_points = earned_points - (this.challenge?.scavenger_hunt_settings?.hint_use_deduction ?? 0);
            }
            location.earned_points = earned_points;
            return location;
        } );
    }

    get gaveUpIds() {
        return this.activated_challenge?.scavenger_hunt_use?.give_up_location_ids.map((id) => id.$oid) ?? [];
    }

    get usedHintIds() {
        return this.activated_challenge?.scavenger_hunt_use?.hint_location_ids.map((id) => id.$oid) ?? [];
    }

    getCheckinsDetails(ps: GetPointResponse[]) {
        this.activated_checkin_details = [];
        if (isDefined(this.activated_challenge?.checkin_locations)) {
            this.activated_challenge?.checkin_locations.forEach((checkin_location) => {
                const checkin = this.allCheckins.get(getCheckinKey(checkin_location));

                if (!isDefined(checkin)) return;

                const location = this.challenge?.locations.find(
                    (l) => l.location?.place_id === checkin_location.place_id
                    && l.location?.latLng.lat === checkin_location.latLng.lat
                    && l.location?.latLng.lng === checkin_location.latLng.lng
                );

                const point = ps.find(
                    (p) => p.location.place_id === checkin_location.place_id
                    && p.location.latLng.lat === checkin_location.latLng.lat
                    && p.location.latLng.lng === checkin_location.latLng.lng
                );

                const category = topicData.categories.find((c) => c._id?.$oid === point?.topic_category_id.$oid);

                const counted_checkins = this.activated_challenge?.counted_checkins?.filter((c) => c.location_id.$oid === location?._id?.$oid);

                const receipts = this.activated_challenge?.receipts?.filter((receipt) => receipt.challenge_location_id.$oid === location?._id?.$oid) ?? [];

                this.activated_checkin_details.push({
                    all_timestamps: checkin.map((c) => c.timestamp ?? ''),
                    counted_timestamps: counted_checkins?.map((c) => c.timestamp ?? '') ?? [],
                    categoryIcon: category?.icon ?? DEFAULT_CATEGORY_ICON,
                    categoryName: category?.name ?? '',
                    placeName: point?.name ?? point?.location.search ?? '',
                    pointValue: location?.value ?? 0,
                    location: location?.location,
                    challenge_location_id: location?._id,
                    receipts
                });
            });
        }
    }

    async getLocationsDistanceFromUser(user_location: { lat: number; lng: number }): Promise<void> {
        if (!this.challenge) return;
        
        // PERFORMANCE: Early return if Google Maps isn't available
        if (typeof google === 'undefined' || !isDefined(google.maps?.geometry)) {
            console.warn('Google Maps geometry not available for distance calculation');
            return;
        }

        // if no user location, return original list
        if (!isDefined(user_location)) {
            getMainPage().errorToastElement.showToast('Unable to get your location for distance calculation.');
            return;
        }
        
        // PERFORMANCE: Process in batches to avoid blocking UI with thousands of locations
        const BATCH_SIZE = 50;
        const result: ChallengeLocationSummary[] = [];

        for (let i = 0; i < this.challengeLocations.length; i += BATCH_SIZE) {
            const batch = this.challengeLocations.slice(i, i + BATCH_SIZE);
            
            const processedBatch = batch.map((location) => {
                const locationData = location.challenge_location_info?.location;
                if (!isDefined(locationData)) {
                    console.warn(`Location data missing for location ID: ${location.challenge_location_info?._id?.$oid ?? ''}`);
                    return {
                        ...location,
                        distance: undefined
                    };
                }
                const locationLatLng = locationData.latLng ?? { lat: 0, lng: 0 };
                const distance = google.maps.geometry.spherical.computeDistanceBetween(
                    new google.maps.LatLng(locationLatLng.lat, locationLatLng.lng),
                    new google.maps.LatLng(user_location.lat, user_location.lng)
                );
                return {
                    ...location,
                    distance
                };
            });
            
            result.push(...processedBatch);

            // Yield control back to browser every batch to keep UI responsive
            if (i + BATCH_SIZE < this.challengeLocations.length) {
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }

        this.challengeLocations = result;
    }

    async getLocationsWithGeoEligibility(): Promise<ChallengeLocationSummary[]> {
        if (!this.challenge) return this.challengeLocations;

        // PERFORMANCE FIX: Early returns for common cases
        if (typeof google === 'undefined' || !isDefined(this.challenge.geofence_radius_m)) {
            return this.challengeLocations;
        }

        if (!isDefined(google.maps?.geometry)) {
            return this.challengeLocations;
        }

        // PERFORMANCE FIX: Cache user location and don't re-fetch if recent
        const LOCATION_CACHE_TIME = 30000; // 30 seconds
        const cachedLocation = this.getCachedUserLocation();

        let user_location: { lat: number, lng: number } | undefined;

        if (cachedLocation && (Date.now() - cachedLocation.timestamp) < LOCATION_CACHE_TIME) {
            user_location = cachedLocation.location;
        } else {
            try {
                user_location = await this.getUserLocationWithTimeout(3000); // Reduced timeout
                this.setCachedUserLocation(user_location);
            } catch (e) {
                console.error('Failed to get user location', e);
                user_location = undefined;
            }
        }

        // PERFORMANCE FIX: Process in smaller batches to avoid blocking UI
        return await this.processLocationsBatched(user_location);
    }

    // PERFORMANCE FIX: Add location caching
    private cachedUserLocation?: { location: { lat: number, lng: number }, timestamp: number };

    private getCachedUserLocation() {
        return this.cachedUserLocation;
    }

    private setCachedUserLocation(location: { lat: number, lng: number }) {
        this.cachedUserLocation = {
            location,
            timestamp: Date.now()
        };
    }

    // PERFORMANCE FIX: Reduced timeout and better error handling
    private async getUserLocationWithTimeout(timeout: number = 3000): Promise<{ lat: number, lng: number }> {
        return await Promise.race([
            new Promise<{ lat: number, lng: number }>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const lat = position.coords.latitude;
                        const lng = position.coords.longitude;
                        pointCache.userLocation = { lat, lng };
                        resolve({ lat, lng });
                    },
                    (error) => reject(error),
                    {
                        enableHighAccuracy: false, // Faster, less accurate
                        timeout: timeout - 500,
                        maximumAge: 60000 // Use cached location up to 1 minute old
                    }
                );
            }),
            new Promise<never>((resolve, reject) =>
                setTimeout(() => reject(new Error('Location request timed out')), timeout)
            )
        ]);
    }

    // PERFORMANCE FIX: Process locations in batches to avoid blocking UI
    private async processLocationsBatched(user_location?: { lat: number, lng: number }): Promise<ChallengeLocationSummary[]> {
        const BATCH_SIZE = 10;
        const result: ChallengeLocationSummary[] = [];

        for (let i = 0; i < this.challengeLocations.length; i += BATCH_SIZE) {
            const batch = this.challengeLocations.slice(i, i + BATCH_SIZE);
            const processedBatch = batch.map(location => this.processLocationGeofence(location, user_location));
            result.push(...processedBatch);

            // Yield control back to browser every batch
            if (i + BATCH_SIZE < this.challengeLocations.length) {
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }

        return result;
    }

    private processLocationGeofence(location: ChallengeLocationSummary, user_location?: { lat: number, lng: number }): ChallengeLocationSummary {
        const canGeofenceCheckin = location.challenge_location_info?.can_geofence_checkin ?? true;
        let distance;
        let isInsideGeofence = false;
        const locationObj = location.challenge_location_info?.location;

        if (!isDefined(locationObj)) {
            return {
                ...location,
                geo_location_eligible: false,
                distance: undefined
            };
        }

        if (canGeofenceCheckin && user_location && isDefined(location.challenge_location_info?.location)) {
            if (isDefined(location?.point_info?.custom_shape)) {
                try {
                    if (location.point_info?.custom_shape === undefined) {
                        throw new Error('Custom shape is undefined');
                    }
                    const result = this.isInsideCustomShape(user_location, location.point_info?.custom_shape);
                    isInsideGeofence = result;
                    const find_distance = this.isInsideGeofence(user_location, locationObj);
                    distance = find_distance.distance ?? undefined;
                } catch (e) {
                    const result = this.isInsideGeofence(user_location, locationObj);
                    isInsideGeofence = result.boolean;
                    distance = result.distance ?? undefined;
                }
            } else {
                const result = this.isInsideGeofence(user_location, locationObj);
                isInsideGeofence = result.boolean;
                distance = result.distance ?? undefined;
            }
        }

        return {
            ...location,
            geo_location_eligible: isInsideGeofence,
            distance
        };
    }

    isInsideGeofence(user_location: { lat: number | google.maps.LatLng | google.maps.LatLngLiteral; lng: number | boolean | null | undefined; }, location: Location) {
        const locationLatLng = location.latLng ?? { lat: 0, lng: 0 };
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(locationLatLng.lat, locationLatLng.lng),
            new google.maps.LatLng(user_location.lat, user_location.lng)
        );
        return {boolean: distance <= (this.challenge?.geofence_radius_m ?? 0), distance};
    }

    isInsideCustomShape(user_location: { lat: number | google.maps.LatLng | google.maps.LatLngLiteral; lng: number | boolean | null | undefined; }, shape: CustomShape) {
        const google_shape = createGoogleMapShapeFromCustomShape(shape);
        if (google_shape instanceof google.maps.Polygon) {
            return google.maps.geometry.poly.containsLocation(
                new google.maps.LatLng(user_location.lat, user_location.lng),
                google_shape
            );
        } else if (Array.isArray(google_shape)) {
            for (const element of google_shape) {
                if (element instanceof google.maps.Polygon) {
                    if (google.maps.geometry.poly.containsLocation(
                        new google.maps.LatLng(user_location.lat, user_location.lng),
                        element
                    )) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    get filtersApplied() {
        return this.location_sort_filter.category_names.length > 0
            || isDefined(this.location_sort_filter.point_value_min)
            || isDefined(this.location_sort_filter.point_value_max)
            || (this.location_sort_filter.search?.length ?? 0) > 0
            || (this.location_sort_filter.tags?.tags.length ?? 0) > 0;
    }

    applySortFilter(filter?: Partial<ChallengeLocationFilters>) {
        this.location_sort_filter = {
            ...this.location_sort_filter,
            ...filter
        };

        // PERFORMANCE FIX: Only filter if we actually have locations
        if (this.challengeLocations.length === 0) {
            this.filteredChallengeLocations = [];
            return;
        }

        this.filteredChallengeLocations = this.challengeLocations;

        // Apply filters in order of cost (cheapest first)

        // 1. Simple property checks first - FIX: Apply each filter individually
        this.filteredChallengeLocations = this.filteredChallengeLocations.filter((location) => {
            const checked_in_or_pending = (location.checked_in ?? false) ||
                (this.activated_challenge?.pending_checkin_location_ids?.includes(location.challenge_location_info?._id?.$oid ?? '') ?? false);
            const active = location.challenge_location_info?.is_active === true;

            let passesFilters = true;

            passesFilters = passesFilters && active;

            // Only apply checked_in filter if it's explicitly set (not the default false)
            if (this.location_sort_filter.checked_in !== undefined) {
                passesFilters = passesFilters && (this.location_sort_filter.checked_in === 'all' || this.location_sort_filter.checked_in === checked_in_or_pending);
            }

            // Apply geo location filter
            if (this.location_sort_filter.geo_location_eligible === true) {
                passesFilters = passesFilters && (location.geo_location_eligible ?? false);
            }

            // Apply point value filters
            if (isDefined(this.location_sort_filter.point_value_min)) {
                passesFilters = passesFilters && ((location.challenge_location_info?.value ?? 0) >= this.location_sort_filter.point_value_min);
            }

            if (isDefined(this.location_sort_filter.point_value_max)) {
                passesFilters = passesFilters && ((location.challenge_location_info?.value ?? 0) <= this.location_sort_filter.point_value_max);
            }

            return passesFilters;
        });

        // 2. More expensive operations only if needed
        if (this.location_sort_filter.category_names.length > 0) {
            this.filteredChallengeLocations = this.filteredChallengeLocations.filter((location) => {
                return isDefined(location.point_info) ? categoryFilterFn(location.point_info, this.location_sort_filter.category_names) : false;
            });
        }

        if (isDefined(this.location_sort_filter.tags)) {
            this.filteredChallengeLocations = this.filteredChallengeLocations.filter((location) => {
                return isDefined(location.point_info) && this.location_sort_filter.tags ? tagsFilterFn(location.point_info, this.location_sort_filter.tags) : false;
            });
        }

        // 3. Most expensive operation last (search)
        if (this.location_sort_filter.search && this.location_sort_filter.search.length > 0) {
            this.filteredChallengeLocations = this.filteredChallengeLocations.filter((location) => {
                if (!isDefined(location.point_info)) {
                    return false;
                }
                return keywordSearchFn(location.point_info, this.location_sort_filter.search ?? []);
            });
        }

        // PERFORMANCE FIX: Cache sorted points reference to avoid repeated calls
        const sortedPoints = pointCache.getSortedPoints(undefined, true);

        this.filteredChallengeLocations = this.filteredChallengeLocations.sort((a, b) => {
            const pointA = a.point_info;
            const pointB = b.point_info;

            const indexA = sortedPoints.findIndex(point => point === pointA);
            const indexB = sortedPoints.findIndex(point => point === pointB);

            return indexA - indexB;
        });
        // Apply distance/value sorting
        this.applySortingOptions();
    }

    // PERFORMANCE FIX: Extract sorting to separate method
    private applySortingOptions() {
        if (this.location_sort_filter.nearby_sort === 'asc' || this.location_sort_filter.nearby_sort === 'desc') {
            this.filteredChallengeLocations = this.filteredChallengeLocations.sort((a, b) => {
                const distanceA = a.distance ?? 0;
                const distanceB = b.distance ?? 0;
                return this.location_sort_filter.nearby_sort === 'asc' ? distanceA - distanceB : distanceB - distanceA;
            });
        }

        if (this.location_sort_filter.value_sort === 'asc' || this.location_sort_filter.value_sort === 'desc') {
            this.filteredChallengeLocations = this.filteredChallengeLocations.sort((a, b) => {
                const valueA = a.challenge_location_info?.value ?? 0;
                const valueB = b.challenge_location_info?.value ?? 0;
                return this.location_sort_filter.value_sort === 'asc' ? valueA - valueB : valueB - valueA;
            });
        }
    }

    get nextUnCheckedInLocation() {
        // put challenge locations in order of challenge
        const original_order = this.challenge?.locations.map((location) => {
            return this.challengeLocations.find((l) => l.challenge_location_info?._id?.$oid === location._id?.$oid);
        } ).filter((location) => location !== undefined) as ChallengeLocationSummary[];
        // find the first location that is not checked in
        const skip_if_give_up = this.challenge?.scavenger_hunt_settings?.give_up_behavior === 'skip_location';
        return original_order.find((location) => location.checked_in !== true && location.gave_up !== skip_if_give_up);
    }

    get gaveUpLocationsUnCheckedIn() {
        return this.filteredChallengeLocations.filter((location) => location.gave_up === true && !(location.checked_in ?? false));
    }

    isType(type: ChallengeType): boolean {
        return this.challenge?.challenge_type === type;
    }

    clearSortFilter() {
        this.location_sort_filter = {
            nearby_sort: undefined, // FIX: Reset to undefined instead of 'asc'
            distance: undefined,
            active: true,
            checked_in: 'all',
            geo_location_eligible: false,
            value_sort: undefined, // FIX: Reset to undefined instead of 'asc'
            point_value_min: undefined,
            point_value_max: undefined,
            category_names: [],
            tags: undefined,
            search: undefined
        };
        this.filteredChallengeLocations = this.challengeLocations.filter((location) => location.challenge_location_info?.is_active !== false);
    }

    async getRegistrationLink() {
        // Prefer the server-provided registration_link when available
        if (this.challenge?.registration_link) {
            let link = this.challenge.registration_link;
            if (topicData.verifiedAlternateHost) {
                link = topicData.swapUrlForOrgUrl(link);
            }
            return link;
        }

        await topicData.getCommandCenterShareLink();
        let link = topicData.publicShareLink;

        if (!link) {
            throw new Error('No registration form found');
        }

        // if there is a customURL, use that instead
        if (topicData.verifiedAlternateHost) {
            link = topicData.swapUrlForOrgUrl(link);
        }

        return `${link}?view=registration&challenge_id=${this.challenge?._id?.$oid ?? ''}`;
    }

    async getCheckinLinkForLocation(id: string) {
        const active_locations = this.filteredChallengeLocations.filter((location) => location.challenge_location_info?.is_active === true);
        const location = active_locations.find(location => location.challenge_location_info?._id?.$oid === id);
        await topicData.getCommandCenterShareLink();

        if (!isDefined(location)) {
            getMainPage().errorToastElement?.showToast('Location not found when attempting to check in.');
            return '';
        }

        let link = topicData.publicShareLink;

        if (!link) {
            throw new Error('No registration form found');
        }

        // if there is a customURL, use that instead
        if (topicData.verifiedAlternateHost) {
            link = topicData.swapUrlForOrgUrl(link);
        }

        return `${link}?view=challenge&challenge_location_qr_code_key=${location.challenge_location_info?.qr_code_key ?? ''}`;
    }

    async getHintForLocation(id: string) {

        if (!isDefined(location) || !isDefined(this.activatedChallengeId) || !isDefined(this.activatedChallengeKey)) {
            getMainPage().errorToastElement?.showToast('Location not found when attempting to get hint.');
            return '';
        }

        await get_hunt_hint(this.activatedChallengeId, this.activatedChallengeKey, id);
        const activated_challenge = await getActivatedChallenge(ChallengeData.amplitude_callsite, this.activatedChallengeId, this.activatedChallengeKey);
        this.activated_challenge = activated_challenge;
    }

    async giveUpOnLocation(id: string) {

        if (!isDefined(location) || !isDefined(this.activatedChallengeId) || !isDefined(this.activatedChallengeKey)) {
            getMainPage().errorToastElement?.showToast('Location not found when attempting to give up.');
            return '';
        }

        await get_hunt_give_up(this.activatedChallengeId, this.activatedChallengeKey, id);
        const activated_challenge = await getActivatedChallenge(ChallengeData.amplitude_callsite, this.activatedChallengeId, this.activatedChallengeKey);
        this.activated_challenge = activated_challenge;
        await this.refreshChallenge();
    }

    async getRegistrationQrCode() {
        const link = await this.getRegistrationLink();

        const qr = (window as any).qrcode(0 /* type */, 'L' /* error correction level */);
        qr.addData(link);
        qr.make();
        const blobDataUrl = qr.createDataURL(8 /* cell size */, 32 /* margin */);

        const fileName = `${topicData.name.replace(/[^a-zA-Z0-9 ]/g, '').replace(/ /g, '_').toLowerCase().substring(0, 20) ?? ''}_qr_code.gif`;

        return {
            challengeName: this.challenge?.name,
            fileName,
            blobDataUrl
        };
    }

    /**
     * Generate the leaderboard embed URL for this challenge
     * Uses publicShareLink (which includes topic_key) and swapUrlForOrgUrl for custom domains
     */
    getChallengeLeaderboardLink() {
        if (!this.challenge?._id?.$oid) {
            return '';
        }

        const baseUrl = topicData.publicShareLink.split('?')[0];
        let embedUrl = `${baseUrl}?view=challenge-leaderboard&challenge_id=${this.challenge._id.$oid}`;
        embedUrl = topicData.swapUrlForOrgUrl(embedUrl);
        
        return embedUrl;
    }

    /**
     * Generate the photo feed embed URL for this challenge
     * Uses publicShareLink (which includes topic_key) and swapUrlForOrgUrl for custom domains
     */
    getChallengePhotoFeedLink(columns: number = 3) {
        if (!this.challenge?._id?.$oid) {
            return '';
        }

        const baseUrl = topicData.publicShareLink.split('?')[0];
        let embedUrl = `${baseUrl}?view=challenge-photo-feed&challenge_id=${this.challenge._id.$oid}&columns=${columns}`;
        embedUrl = topicData.swapUrlForOrgUrl(embedUrl);
        
        return embedUrl;
    }

    get savedLocationIds() {
        return this.activated_challenge?.personal_locations?.saved.map((loc) => loc.$oid) ?? [];
    }

    get locationsComplete() {
        const completed =  this.challengeLocations.filter((location) => location.checked_in === true && location.challenge_location_info?.is_active === true).length;
        // if it isa number, return it, otherwise return 0
        return isDefined(completed) && isNumber(completed) ? completed : 0;
    }

    get locationsGivenUp() {
        return this.challengeLocations.filter((location) => location.gave_up === true && location.challenge_location_info?.is_active === true).length;
    }

    get totalActiveLocations() {
        return this.challengeLocations.filter((location) => location.challenge_location_info?.is_active === true).length;
    }

    get allLocationsCheckedIn() {
        return this.locationsComplete === this.totalActiveLocations;
    }

    /// Payment Helpers
    async getSpecificUserPurchasesAsAdmin(activated_challenge_id: string): Promise<ActivatedChallengePurchasesResponse> {
        return (await get_activated_challenge_purchases(
                activated_challenge_id,
                topicData.topic?.is_writer === true ? undefined : this.activatedChallengeKey
            )) ?? { organization_subscriptions: [], transaction_history: [] };
    }


    async getThisUserPurchases(refresh: boolean = false): Promise<ActivatedChallengePurchasesResponse> {
        if (!isDefined(this.thisUserPurchases) || refresh) {
            this.thisUserPurchases = (await get_activated_challenge_purchases(
                this.activatedChallengeId ?? '',
                topicData.topic?.is_writer === true ? undefined : this.activatedChallengeKey
            )) ?? { organization_subscriptions: [], transaction_history: [] };
        }
        return this.thisUserPurchases ?? { organization_subscriptions: [], transaction_history: [] };
    }

    thisUserHasPaidSomething() {
        if (isDefined(this.activated_challenge) && this.activated_challenge.payment_details?.payment_status === ChargebeeTransactionStatus.AdminOverridden) {
            return true;
        }
        if (isDefined(this.thisUserPurchases)) {
            for (const purchase of this.thisUserPurchases.transaction_history) {
                if (purchase.status === PurchaseStatus.Completed) {
                    // If this purchase has a subscription, check if the subscription is still active
                    if (purchase.subscription_id) {
                        const subscription = this.thisUserPurchases.organization_subscriptions.find(
                            sub => sub.subscription_id === purchase.subscription_id
                        );
                        // Only count as paid if subscription exists and is not cancelled
                        if (subscription && subscription.status !== 'cancelled') {
                            return true;
                        }
                    } else {
                        // One-time purchase with completed status counts as paid
                        return true;
                    }
                }
            }
        }
        return false;
    }

    async getProductsForChallenge(): Promise<GetPublicOrganizationProductResponse[]> {
        if (!isDefined(this.challenge)) {
            throw new Error('Challenge not found');
        }
        let products: GetPublicOrganizationProductResponse[] = [];
        try {
            products = await getOrganizationProductsPublic(PaymentTargetType.ActivatedChallenge, this.challenge._id.$oid, topicData.organizationId ?? '');
        } catch (e) {
            console.error('Failed to get products for challenge', e);
        }
        this.products = products;
        return this.products;
    }


    async downloadQrCodeForLocation(id: string) {
        const active_locations = this.filteredChallengeLocations.filter((location) => location.challenge_location_info?.is_active === true);
        const location = active_locations.find(location => location.challenge_location_info?._id?.$oid === id);

        if (!isDefined(location)) {
            getMainPage().errorToastElement?.showToast('Location not found when attempting to download QR code.');
            return {
                challengeName: this.challenge?.name,
                pointName: 'point',
                fileName: 'qr_code.gif',
                blobDataUrl: ''
            };
        }

        const link = await this.getCheckinLinkForLocation(id);

        const qr = (window as any).qrcode(0 /* type */, 'L' /* error correction level */);
        qr.addData(link);
        qr.make();
        const blobDataUrl = qr.createDataURL(8 /* cell size */, 32 /* margin */);

        let fileName = 'qr_code.gif';
        let pointName = 'point';
        if (location.challenge_location_info?.location) {
            const point = pointCache.getPointsFromLocation(location.challenge_location_info?.location)?.[0];

            pointName = point?.name ?? point?.location.search ?? 'point';
            fileName = `${pointName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/ /g, '_').toLowerCase().substring(0, 20) ?? ''}_qr_code.gif`;
        }

        return {
            challengeName: this.challenge?.name,
            pointName,
            fileName,
            blobDataUrl
        };
    }

    // helpers
    allPlacesHaveBeenVisited() {
        // if we havent loaded yet return false
        if (!challengeData.isReady || challengeData.filteredChallengeLocations.length === 0) {
            return false;
        }
        return challengeData.allLocationsCheckedIn;
    }

    // Internal Note Calls
    async createInternalNoteForChallengePoint(point_id: string, content: string) {
        if (!isDefined(this.challenge)) {
            throw new Error('Challenge not found');
        }
        return await createInternalChallengeNote(point_id, content, this.activatedChallengeId ?? '', this.activatedChallengeKey ?? '');
    }

    async getInternalNotesForChallengePoint(point_id: string) {
        if (!isDefined(this.challenge)) {
            throw new Error('Challenge not found');
        }
        return await getInternalChallengeNotes(point_id, this.activatedChallengeId ?? '', this.activatedChallengeKey ?? '');
    }

    async deleteInternalNoteForChallengePoint(note_id: string) {
        if (!isDefined(this.challenge)) {
            throw new Error('Challenge not found');
        }
        return await deleteInternalChallengeNote(note_id, this.activatedChallengeId ?? '', this.activatedChallengeKey ?? '');
    }

    async updateInternalNoteForChallengePoint(note_id: string, content: string) {
        if (!isDefined(this.challenge)) {
            throw new Error('Challenge not found');
        }
        return await updateInternalChallengeNote(note_id, content, this.activatedChallengeId ?? '', this.activatedChallengeKey ?? '');
    }
}

const challengeData = new ChallengeData();

export default challengeData;

export function challengeTypeName(challenge: GetChallengeResponse | undefined) {
    return challenge?.challenge_type === 'passport' ? 'passport' :
        challenge?.challenge_type === 'hunt' ? 'scavenger hunt' :
        challenge?.challenge_type === 'trivia' ? 'trivia challenge' :
        challenge?.challenge_type === 'guide' ? 'guide' :
        challenge?.challenge_type === 'treasure' ? 'treasure hunt' :
        'challenge';
}

export function challengeTypeDisplayName(challenge: GetChallengeResponse | undefined) {
    return challenge?.challenge_type === 'passport' ? 'Passport' :
        challenge?.challenge_type === 'hunt' ? 'Scavenger Hunt' :
        challenge?.challenge_type === 'trivia' ? 'Trivia Challenge' :
        challenge?.challenge_type === 'guide' ? 'Guide' :
        challenge?.challenge_type === 'treasure' ? 'Treasure Hunt' :
        'Challenge';
}

export function challengeLocationsDisplayName(challenge: GetChallengeResponse | undefined, plural: boolean = false, capitalization: 'title' | 'lower' = 'lower') {
    let term = '';
    switch (challenge?.challenge_type) {
        case 'passport':
            term = plural ? (capitalization === 'title' ? 'Locations' : 'locations') : (capitalization === 'title' ? 'Location' : 'location');
            break;
        case 'hunt':
            term = plural ? (capitalization === 'title' ? 'Clues' : 'clues') : (capitalization === 'title' ? 'Clue' : 'clue');
            break;
        case 'trivia':
            term = plural ? (capitalization === 'title' ? 'Clues' : 'clues') : (capitalization === 'title' ? 'Clue' : 'clue');
            break;
        case 'guide':
            term = plural ? (capitalization === 'title' ? 'Locations' : 'locations') : (capitalization === 'title' ? 'Location' : 'location');
            break;
        case 'treasure':
            term = plural ? (capitalization === 'title' ? 'Stops' : 'stops') : (capitalization === 'title' ? 'Stop' : 'stop');
            break;
        default:
            term = plural ? (capitalization === 'title' ? 'Locations' : 'locations') : (capitalization === 'title' ? 'Location' : 'location');
            break;
    }
    return term;
}

export function challengeNotStarted(challenge?: GetChallengeResponse, isTester?: boolean) : boolean {
    if (isTester === true) return false;
    const startTime = challenge?.start_time;
    if (!startTime) return challenge?.is_live === false;
    return new Date() < new Date(startTime);
}

export function challengeHasEnded(challenge?: GetChallengeResponse, isTester?: boolean) : boolean {
    if (isTester === true) return false;
    const endTime = challenge?.end_time;
    if (!endTime) return challenge?.is_live === false;
    return new Date() > new Date(endTime);
}

export function challengeNotLive(challenge?: GetChallengeResponse, isTester?: boolean) : boolean {
    if (isTester === true) return false;
    return (challenge?.is_live === false);
}